import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaBrain, 
  FaLanguage, 
  FaShieldAlt, 
  FaChartLine,
  FaUsersCog, 
  FaBolt,
  FaRobot,
  FaGamepad
} from 'react-icons/fa';

const FeaturesSection = styled.section`
  padding: 5rem 2rem;
  position: relative;
  background: #ffffff;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(8, 145, 178, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(6, 182, 212, 0.02) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #0891b2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto;
  font-weight: 500;
`;

const FeaturesGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureCard = styled(motion.div)`
  background: #ffffff;
  border: 2px solid #f0f0f0;
  border-radius: 20px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    border-color: #0891b2;
    
    &::before {
      opacity: 1;
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0891b2, #06b6d4);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 15px rgba(8, 145, 178, 0.2);
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
  font-weight: 700;
`;

const FeatureDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
  font-weight: 400;
`;

const HighlightedFeature = styled(motion.div)`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0891b2;
  border-radius: 20px;
  padding: 3rem;
  margin-top: 4rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  box-shadow: 0 8px 30px rgba(8, 145, 178, 0.1);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 2rem 1.5rem;
    gap: 2rem;
  }
`;

const HighlightContent = styled.div``;

const HighlightTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
  font-weight: 700;
`;

const HighlightDescription = styled.p`
  color: #6b7280;
  line-height: 1.8;
  margin-bottom: 2rem;
  font-weight: 500;
`;

const HighlightStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }
`;

const StatItem = styled.div`
  background: #ffffff;
  border: 2px solid #f0f0f0;
  padding: 1rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0891b2;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
`;

const features = [
  {
    icon: <FaBrain />,
    title: "No Workflow Configuration",
    description: "Configure your AI assistant using natural language - no complicated workflows or decision trees. Simply describe what you want in free text and let our AI understand and adapt."
  },
  {
    icon: <FaLanguage />,
    title: "Omnichannel Conversations",
    description: "Manage continuous conversations across web, mobile, and social media messaging channels. Your players get consistent support wherever they prefer to engage."
  },
  {
    icon: <FaUsersCog />,
    title: "AI & Human Cooperation",
    description: "Seamless handover between AI and human agents with continuous learning. The system learns from how your agents handle issues and automatically improves its knowledge base."
  },
  {
    icon: <FaChartLine />,
    title: "Business Intelligence & Insights",
    description: "Understand what's on your players' minds with real-time conversation analytics. Spot trends, identify spikes in issues, and get actionable insights from every interaction."
  },
  {
    icon: <FaShieldAlt />,
    title: "Risk & Compliance Management",
    description: "Built-in responsible gaming features, AML monitoring, and automated compliance across all jurisdictions."
  },
  {
    icon: <FaBolt />,
    title: "Real-Time Intervention",
    description: "Prevent churn with intelligent triggers that identify at-risk players and automatically engage them with personalized offers."
  }
];

const Features = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <FeaturesSection id="features">
      <Container>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Built for the Unique Needs of iGaming
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Every feature designed with online casinos and sports betting platforms in mind
          </SectionSubtitle>
        </SectionHeader>

        <FeaturesGrid
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>

        <HighlightedFeature
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <HighlightContent>
            <HighlightTitle>
              <FaGamepad style={{ marginRight: '0.5rem', color: '#3b82f6' }} />
              Game-Aware Intelligence
            </HighlightTitle>
            <HighlightDescription>
              Faroflow doesn't just answer questions – it understands the context of your casino. 
              From explaining slot volatility to guiding players through bonus wagering requirements, 
              our AI speaks the language of iGaming fluently.
            </HighlightDescription>
            <HighlightStats>
              <StatItem>
                <StatValue>500+</StatValue>
                <StatLabel>Casino Games Known</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>100+</StatValue>
                <StatLabel>Payment Methods</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>50+</StatValue>
                <StatLabel>Jurisdictions</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>24/7</StatValue>
                <StatLabel>Uptime Guarantee</StatLabel>
              </StatItem>
            </HighlightStats>
          </HighlightContent>
          <HighlightContent style={{ textAlign: 'center' }}>
            <motion.div
              animate={{
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <FaRobot style={{ fontSize: '10rem', color: '#3b82f6', opacity: 0.3 }} />
            </motion.div>
          </HighlightContent>
        </HighlightedFeature>
      </Container>
    </FeaturesSection>
  );
};

export default Features; 